// @ts-strict-ignore
import _ from 'lodash';
import { APP_STATE, IS_PROTRACTOR } from '@/main/app.constants';
import { logError, logInfo } from '@/hybrid/utilities/logger';
import { formatMessage } from '@/hybrid/utilities/logger.utilities';
import { close as socketClose, waitForOpen } from '@/hybrid/utilities/socket.utilities';
import { HeadlessCategory, isHeadlessCategory } from '@/services/headlessCapture.utilities';
import { headlessRenderMode, waitForAppQuiescence, waitForPluginQuiescence } from '@/hybrid/utilities/utilities';
import { getAxiosInstance } from '@/hybrid/requests/axios.utilities';
import { cancelAll } from '@/hybrid/requests/pendingRequests.utilities';
import { getStorageSafely } from '@/hybrid/utilities/storage.utilities';
import {
  Visualization,
  VisualizationData,
} from '@/hybrid/annotation/ckEditorPlugins/components/content.utilities.constants';
import { onAllServerRequestsCanceled } from '@/services/notifier.service';
import { PERSISTENCE_LEVELS } from '@/core/flux.service';

/**
 * @file Requests and manages screenshots.
 *
 * This service has some functions which only run when the app is running in the headless browser used by
 *   NodeJS to capture screenshots. The 'callback' mechanism of that browser is used to send
 *   messages to NodeJS to inform it when the screenshot is ready to be taken.
 */

// Screenshot rendering only supports a small subset of paths within the application, if the headless browser is
// redirected to a page that isn't allowed the capture should fail. The load-error page is allowed because it has
// special handling for failing the headless capture with a relevant message.
export const ALLOWED_SCREENSHOT_MODE_PATHS = /^\/(present|headless-capture-standby|load-error|report-template)/;

let captureMetadata: { category?: HeadlessCategory } = {};
let deferredWorkbookId;
let deferredWorksheetId;

/**
 * Checks whether any deferred thumbnail generation requests were made that should be replaced with a 'forced'
 * (non-deferred) request, in preparation for leaving the worksheet.
 */
export function onStateExit($state) {
  // If we deferred any thumbnail generation, we make one more thumbnail generation
  // request with deferral set to False so that it is processed immediately without waiting for a timeout.
  // We still require that data come from the nodejs cache; if it isn't in that cache anymore, then the previous
  // thumbnail request will have already expired and generated the thumbnail.
  if (
    $state.current.name === APP_STATE.WORKSHEET &&
    $state.params.workbookId === deferredWorkbookId &&
    $state.params.worksheetId === deferredWorksheetId &&
    $state.params.archived !== true
  ) {
    generate(deferredWorkbookId, deferredWorksheetId, false);
  }
}

/**
 * Tells headless capture browser that the app is ready to capture a screenshot.
 * This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyCapture({ sqReportContent, $$testability }: { sqReportContent: any; $$testability: any }) {
  function waitForQuiescence() {
    return Promise.all([waitForAppQuiescence($$testability, sqReportContent), waitForPluginQuiescence()]);
  }

  if (headlessRenderMode()) {
    // Wait for the app to settle (i.e. no more http requests or timers to fire), then wait 100ms and wait for
    // the app to settle again. This is an attempt to handle views that request data asynchronously some
    // (short) time after the view is first loaded.
    waitForQuiescence()
      .then(function () {
        return new Promise(function (resolve) {
          setTimeout(resolve, 100);
        });
      })
      .then(waitForQuiescence)
      .then(function () {
        window.seeqHeadlessCapture();
      });
  }
}

/**
 * Tells the headless capture browser that the page is loading.
 * This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyLoading() {
  if (headlessRenderMode()) {
    window.seeqHeadlessLoading();
  }
}

/**
 * Tells the headless capture browser that the page encountered an unrecoverable error
 * This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyError(...errors: string[]) {
  if (headlessRenderMode()) {
    window.seeqHeadlessError(...errors);
  }
}

export function notifyWarning(warning: string) {
  if (headlessRenderMode()) {
    window.seeqHeadlessWarning(warning);
  }
}

/**
 * Tells the headless capture browser that the currently-loading workbook has been cancelled by a user or
 * administrator.  This function does nothing unless the app is being run in the headless capture browser.
 */
export function notifyCancellation(...errors: string[]) {
  if (headlessRenderMode()) {
    window.seeqHeadlessCancellation(...errors);
  }
}

/**
 * Calls the node server to trigger a screenshot generation. Does nothing if page is in the process of taking a
 * screenshot. Upon successful generation it updates the screenshot src for the specified worksheet.
 *
 * @param {String} workbookId - id of the workbook
 * @param {String} worksheetId - id of the worksheet
 * @param {Boolean} [defer] - True to tell the server to defer generating a thumbnail for this worksheet until a
 *   deferral time has elapsed where no other thumbnail requests have been received
 */
export function generate(workbookId: string, worksheetId: string, defer = true) {
  if (defer) {
    deferredWorkbookId = workbookId;
    deferredWorksheetId = worksheetId;
  } else {
    deferredWorkbookId = undefined;
    deferredWorksheetId = undefined;
  }
  if (!headlessRenderMode() && workbookId && worksheetId && !IS_PROTRACTOR) {
    getAxiosInstance()
      .post('/thumbnails', {
        workbookId,
        worksheetId,
        defer,
      })
      .catch((e) => logError(formatMessage`Error starting thumbnail generation: ${e}`));
  }
}

/**
 * Fetches the category of headless capture being requested so that it can be accessed synchronously in the page load
 */
export function fetchHeadlessCaptureMetadata() {
  if (!headlessRenderMode()) {
    return Promise.resolve();
  }

  return Promise.resolve(window.seeqHeadlessCategory()).then((category) => {
    if (!isHeadlessCategory(category)) {
      throw new Error(`Error: '${category}' category is unexpected; the frontend enum likely needs to be updated`);
    }
    captureMetadata = {
      category,
    };
  });
}

/**
 * Gets the category of headless capture being requested
 */
export function headlessCaptureMetadata() {
  return captureMetadata;
}

const DEFAULT_VISUALIZATION: VisualizationData = { visualization: Visualization.NONE };
// Exposed for testing
export const DEFAULT_GET_VISUALIZATION_DATA = () => Promise.resolve(DEFAULT_VISUALIZATION);

/**
 * Attaches callbacks to window that puppeteer can call as well as a state change handler that closes the socket
 * and clears local storage. Does nothing if not in screenshot render mode. See custom-typings.d.ts (Window) for
 * more detailed description of the callbacks attached
 */
export function initializeHeadlessCaptureMode(sqStateSynchronizer, $location, $rootScope) {
  if (!headlessRenderMode()) {
    return;
  }

  window.seeqGetVisualizationData = DEFAULT_GET_VISUALIZATION_DATA;

  // Provides a hook that the headless capture browser can use to cancel all requests from this browser
  window.seeqInternalPageCleanup = () => {
    window.seeqGetVisualizationData = DEFAULT_GET_VISUALIZATION_DATA;
    logInfo("Cleaning up internal state via 'window.seeqInternalCleanup'");
    return (
      cancelAll()
        .catch((e) => logError(formatMessage`Error cancelling all requests: ${e}`))
        // Not all screenshots require the socket to be open, but the socket service will get into a bad state if
        // the socket is closed before it is fully established - to prevent this wait for the socket to be open before
        // capturing the screenshot
        .then(() => waitForOpen())
        .catch((e) => logError(formatMessage`Error waiting for socket to open: ${e}`))
        .then(() => {
          socketClose();
          getStorageSafely().clear();

          // Re-initialize all stores. This is necessary since some stores have special logic to retain certain
          // information even when they are rehydrated; search for 'saveState' for examples.

          // This prevents any outstanding calls to fetch state to be ignored if they finish after initialize()
          sqStateSynchronizer.unsetLoadingWorksheet();
          _.forEach(PERSISTENCE_LEVELS, (persistenceLevel) => {
            sqStateSynchronizer.initialize(persistenceLevel);
          });
        })
        .catch((e) => logError(formatMessage`Error cleaning up page state: ${e}`))
    );
  };

  // Provides a hook that the headless capture browser can use to change states without reloading the page
  window.seeqNavigate = (url) => {
    if (!_.isString(url) || !_.startsWith(url, '/')) {
      throw new Error(`seeqNavigate: A relative url is required, but got '${url}'`);
    }

    $rootScope.$apply(() => {
      logInfo(`Navigating to '${url}' via 'window.seeqNavigate'`);
      $location.url(url);
    });
  };

  onAllServerRequestsCanceled(() => {
    notifyError('Administrator canceled all requests');
  });
}
